import type { FC } from 'react';
import React, { useState } from 'react';
import type { WithIntlProps } from 'react-intl';
import { injectIntl, useIntl } from 'react-intl';
import { cssMap, styled } from '@compiled/react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import Button from '@atlaskit/button/button';
import IconButton from '@atlaskit/button/icon/button';
import AddIcon from '@atlaskit/icon/core/add';
import TaskToDoIcon from '@atlaskit/icon/core/task-to-do';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import StatusWorkflowReturnedIcon from '@atlaskit/icon-lab/core/status-workflow-returned';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';

import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { ApprovalsCompletedSpotlight } from '@confluence/approvals/entry-points/ApprovalsCompletedSpotlight.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { SECONDARY_COLORS } from '../colors.ts';
import { sharedi18n } from '../sharedi18n.ts';
import { ContentStateColor, type ColorAppearance } from '../ContentStateColor.tsx';

import { type ContentStateButtonBaseProps, type ContentStateButtonProps } from './types.ts';
import { i18n } from './i18n.ts';
import { determineHoverColor, determineSecondaryColor } from './helpers.ts';

const contentStateNameStyles = xcss({
	marginLeft: 'space.025',
	marginRight: 'space.025',
	marginTop: 'space.0',
	marginBottom: 'space.0',
});

const subtleContentStateNameStyles = xcss({
	font: 'font.body.small',
	color: 'color.text.subtle',
});

const detailPanelContentStateNameStyles = xcss({
	font: 'font.body',
	textOverflow: 'ellipsis',
	overflowX: 'hidden',
	whiteSpace: 'nowrap',
});

const chevronDownIconWrapperStyles = xcss({
	marginRight: 'space.negative.050',
});

const subtleEmptyStateLabelStyles = xcss({
	fontWeight: 'font.weight.regular',
});

const compactEmptyStateIconStyles = xcss({
	marginInlineEnd: 'space.negative.025',
});

const styles = cssMap({
	approvalStatusIcon: {
		paddingInline: token('space.050'),
	},
});
const determineSecondarySSRHex = (primaryColor?: string | null) => {
	const upperCasePrimaryColor = primaryColor?.toUpperCase(); // need toUpper since color we get from BE is lowercase
	switch (upperCasePrimaryColor) {
		case '#2684FF':
			return '#e9f2ff';
		case '#57D9A3':
			return '#dcfff1';
		case '#FFC400':
			return '#fff7d6';
		case '#8777D9':
			return '#f3f0ff';
		case '#FF7452':
			return '#ffeceb';
		default:
			return '#e9f2ff';
	}
};

const getContentStateButtonBackgroundColor = (
	isWarning: boolean,
	isSelected: boolean,
	isHovered: boolean,
	color: string,
	hoverColor: string,
	isPressed: boolean,
) => {
	if (isWarning) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		return process.env.REACT_SSR ? '#ffeceb' : SECONDARY_COLORS.RED;
	}
	if (isSelected) {
		return color;
	}
	if (isPressed) {
		return token('color.background.neutral.subtle.pressed');
	}
	return isHovered ? hoverColor : color;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HasContentStateButton = styled.button<{ isSubtle?: boolean; isDetailsPanel?: boolean }>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	height: (props) => (props.isDetailsPanel ? '24px' : '32px'),
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	paddingLeft: (props) =>
		props.isDetailsPanel
			? token('space.050')
			: !props.isSubtle
				? token('space.075')
				: token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	paddingRight: (props) =>
		props.isDetailsPanel
			? token('space.025') // (Only 2px on the right because there is already 2px right margin after the text)
			: !props.isSubtle
				? token('space.075')
				: token('space.025'),
	color: token('color.text'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	border: 'none',
	outlineColor: token('color.border.focused'),
	'&:disabled': {
		cursor: 'not-allowed',
		opacity: 0.7,
		backgroundColor: token('color.background.disabled'),
	},
});

const ContentStateButtonBase: FC<ContentStateButtonBaseProps> = ({
	triggerProps,
	togglePopup,
	contentState,
	isSelected,
	isWarning,
	userOffline,
	contentStateButtonRef,
	floatingToolbarButtonStyle,
	appearance,
	primaryColor,
	secondaryColor,
	hoverColor,
	showChevron,
	colorAppearance,
	tooltipContent,
	isDetailsPanel,
}) => {
	const intl = useIntl();
	const [isHovered, setIsHovered] = useState<boolean>(false);
	const [isPressed, setIsPressed] = useState<boolean>(false);

	if (!contentState) {
		return null;
	}
	const isContentStateSelectorDisabled = contentState.isCallerPermitted === false || userOffline;
	const buttonStyles = {
		...floatingToolbarButtonStyle,
		backgroundColor: getContentStateButtonBackgroundColor(
			isWarning,
			isSelected,
			isHovered,
			secondaryColor,
			hoverColor,
			isPressed,
		),
	};
	const button = (
		<HasContentStateButton
			{...triggerProps}
			appearance={appearance}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={buttonStyles}
			onClick={togglePopup}
			disabled={isContentStateSelectorDisabled}
			color={secondaryColor}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onMouseDown={() => setIsPressed(true)}
			onMouseUp={() => setIsPressed(false)}
			isSubtle={appearance === 'subtle'}
			isDetailsPanel={isDetailsPanel}
			aria-label={
				expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true)
					? intl.formatMessage(i18n.changeStatusButtonAriaLabel, {
							statusName: contentState.name,
						})
					: undefined
			}
			data-testid="content-state-trigger"
			type="button"
		>
			<ContentStateColor
				dataTestId="button-color-icon"
				appearance={colorAppearance}
				color={primaryColor}
				restrictionLevel={contentState.restrictionLevel}
				isWarning={isWarning}
			/>
			<Box
				xcss={[
					contentStateNameStyles,
					appearance === 'subtle' && subtleContentStateNameStyles,
					isDetailsPanel && detailPanelContentStateNameStyles,
				]}
				testId="content-state-name"
			>
				{contentState.name}
			</Box>
			{showChevron && (
				<Box xcss={chevronDownIconWrapperStyles}>
					<ChevronDownIcon
						color="currentColor"
						spacing="compact"
						label={intl.formatMessage(i18n.showMoreStatuses)}
						testId="content-state-chevron-down-icon"
						size="small"
					/>
				</Box>
			)}
		</HasContentStateButton>
	);

	return (
		<div ref={contentStateButtonRef}>
			{tooltipContent ? (
				<Tooltip content={intl.formatMessage(tooltipContent)} position="bottom">
					{button}
				</Tooltip>
			) : (
				button
			)}
		</div>
	);
};

const ContentStateButtonComponent: FC<ContentStateButtonProps> = ({
	triggerProps,
	togglePopup,
	contentState,
	activeWorkflowStatus,
	isSelected,
	isWarning,
	userOffline,
	contentStateButtonRef,
	intl: { formatMessage },
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState = false,
	isDetailsPanel = false,
	canEdit = true,
	useSubtleEmptyStateLabel = false,
}) => {
	const { isRbacEnabled } = useSpaceRoleMode();
	const [isWorkflowButtonHovered, setIsWorkflowButtonHovered] = useState<boolean>(false);
	const [isWorkflowButtonPressed, setIsWorkflowButtonPressed] = useState<boolean>(false);

	const getSecondaryColor = ({
		primaryColor,
		isNeutralBackground,
	}: {
		primaryColor: string | null;
		isNeutralBackground: boolean;
	}) => {
		if (isNeutralBackground) {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			return process.env.REACT_SSR ? '#00000000' : token('utility.elevation.surface.current');
		} else {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			return process.env.REACT_SSR
				? determineSecondarySSRHex(primaryColor)
				: determineSecondaryColor(primaryColor);
		}
	};

	const getButtonProps = (editorContentState: any) => {
		const primaryColor = editorContentState.color;
		const isVerifiedContentState =
			editorContentState?.restrictionLevel === 'PAGE_OWNER' &&
			editorContentState.name === 'Verified';
		const shouldUseBylineButtonStyleProps = !isDetailsPanel;
		if (shouldUseBylineButtonStyleProps) {
			return {
				appearance: 'subtle',
				primaryColor,
				secondaryColor: getSecondaryColor({ primaryColor, isNeutralBackground: true }),
				hoverColor: token('elevation.surface.hovered'),
				showChevron: false,
				// The verified content state for the byline is special in that due to having a checkmark in it, a larger styling is required over the smaller subtle style
				colorAppearance: isVerifiedContentState
					? ('contentStateButton' as ColorAppearance)
					: ('subtleContentStateButton' as ColorAppearance),
			};
		}

		const secondaryColor = getSecondaryColor({ primaryColor, isNeutralBackground: isDetailsPanel });

		const hoverColor = isDetailsPanel
			? token('elevation.surface.hovered')
			: determineHoverColor(secondaryColor);
		// (Make sure that the colorAppearance is set to the same value as used in the details panel's
		//  read-only version found in next/packages/details-panel/src/components/StatusRow.tsx)
		const colorAppearance = (
			isDetailsPanel ? 'rendererContentState' : 'contentStateButton'
		) as ColorAppearance;

		return {
			appearance: 'default',
			primaryColor,
			secondaryColor,
			hoverColor,
			showChevron: !isDetailsPanel,
			colorAppearance,
		};
	};

	const verifiedRestrictedTooltipContent = isRbacEnabled
		? sharedi18n.verifiedRestrictedRbac
		: sharedi18n.verifiedRestricted;

	if (activeWorkflowStatus) {
		const isChangesRequested = activeWorkflowStatus.id === '3';
		let WorkflowIcon;
		let iconColor;
		if (isChangesRequested) {
			WorkflowIcon = StatusWorkflowReturnedIcon;
			iconColor = token('color.icon.accent.yellow');
		} else if (activeWorkflowStatus.allowsReviews) {
			WorkflowIcon = StatusWorkflowInReviewIcon;
			iconColor = token('color.icon.accent.blue');
		} else {
			WorkflowIcon = CheckCircleIcon;
			iconColor = token('color.icon.accent.lime');
		}

		const shouldUseSubtle = !isDetailsPanel;
		const secondaryColor = shouldUseSubtle
			? token('utility.elevation.surface.current')
			: token('color.background.neutral');

		const hoveredWorkflowBackgroundColor = isWorkflowButtonHovered
			? token('color.background.neutral.subtle.hovered')
			: secondaryColor;
		const workflowBackgroundColor = isWorkflowButtonPressed
			? token('color.background.neutral.subtle.pressed')
			: hoveredWorkflowBackgroundColor;
		const workflowButtonStyles = {
			...floatingToolbarButtonStyle,
			backgroundColor: workflowBackgroundColor,
		};

		const workflowButton = (dismissSpotlight?: () => void) => (
			<HasContentStateButton
				{...triggerProps}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={workflowButtonStyles}
				onClick={() => {
					dismissSpotlight?.();
					togglePopup();
				}}
				onMouseEnter={() => setIsWorkflowButtonHovered(true)}
				onMouseLeave={() => setIsWorkflowButtonHovered(false)}
				onMouseDown={() => setIsWorkflowButtonPressed(true)}
				onMouseUp={() => setIsWorkflowButtonPressed(false)}
				isSelected={isSelected}
				disabled={userOffline}
				isSubtle={shouldUseSubtle}
				isDetailsPanel={isDetailsPanel}
				aria-label={
					expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true)
						? formatMessage(i18n.changeStatusButtonAriaLabel, {
								statusName: activeWorkflowStatus.name,
							})
						: undefined
				}
				data-testid="content-state-trigger"
				type="button"
			>
				<Box xcss={styles.approvalStatusIcon}>
					<WorkflowIcon label="" size="small" color={iconColor} />
				</Box>
				<Box
					xcss={[
						contentStateNameStyles,
						shouldUseSubtle && subtleContentStateNameStyles,
						isDetailsPanel && detailPanelContentStateNameStyles,
					]}
					testId="content-state-name"
				>
					{activeWorkflowStatus.name}
				</Box>
			</HasContentStateButton>
		);

		const buttonNode = (dismissSpotlight?: () => void) =>
			canEdit ? (
				<Tooltip content={formatMessage(i18n.changeStatus)} position="bottom">
					{workflowButton(dismissSpotlight)}
				</Tooltip>
			) : (
				workflowButton(dismissSpotlight)
			);

		return (
			<div ref={contentStateButtonRef}>
				{!isDetailsPanel && expValEquals('confluence_approvals_eap', 'isEnabled', true) ? (
					<ApprovalsCompletedSpotlight>
						{(dismiss) => buttonNode(dismiss)}
					</ApprovalsCompletedSpotlight>
				) : (
					buttonNode()
				)}
			</div>
		);
	}

	if (contentState) {
		const tooltipContent = !canEdit
			? i18n.statusLabel
			: contentState.restrictionLevel === 'PAGE_OWNER'
				? verifiedRestrictedTooltipContent
				: i18n.changeStatus;
		const buttonProps = getButtonProps(contentState);
		return (
			<Inline space="space.0">
				<ContentStateButtonBase
					triggerProps={triggerProps}
					togglePopup={togglePopup}
					contentState={contentState}
					isSelected={isSelected}
					isWarning={isWarning}
					userOffline={userOffline}
					contentStateButtonRef={contentStateButtonRef}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					tooltipContent={tooltipContent}
					isDetailsPanel={isDetailsPanel}
					{...buttonProps}
				/>
			</Inline>
		);
	}

	// No content state case, but when we want it to render a + icon instead (for details panel)
	if (renderPlusButtonEmptyState) {
		return (
			<IconButton
				{...triggerProps}
				icon={AddIcon}
				appearance="subtle"
				label={formatMessage(i18n.statusLabel)}
				onClick={togglePopup}
				testId="content-state-empty-state-add-icon"
			/>
		);
	}

	// No content state case
	return (
		<div ref={contentStateButtonRef}>
			<Box>
				<Button
					{...triggerProps}
					appearance="subtle"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={
						useSubtleEmptyStateLabel
							? {
									...(floatingToolbarButtonStyle || {}),
									height: 32,
									display: 'flex',
									alignItems: 'center',
								}
							: floatingToolbarButtonStyle || {}
					}
					onClick={togglePopup}
					iconBefore={
						useSubtleEmptyStateLabel ? (
							<Box xcss={compactEmptyStateIconStyles}>
								<TaskToDoIcon label="" testId="content-state-task-icon" />
							</Box>
						) : (
							<TaskToDoIcon label="" testId="content-state-task-icon" />
						)
					}
					// The post-delete placeholder shouldn't show a selected background when the menu
					// is open, matching the other byline items which don't change colour.
					isSelected={useSubtleEmptyStateLabel ? false : isSelected}
					// Default spacing gives the 32px button height that matches the applied-status
					// pill, so the post-delete placeholder keeps the byline row height consistent.
					// Other empty states keep compact spacing.
					spacing={useSubtleEmptyStateLabel ? 'default' : 'compact'}
					testId="content-state-trigger"
					isDisabled={userOffline}
				>
					{useSubtleEmptyStateLabel ? (
						<Box as="span" xcss={subtleEmptyStateLabelStyles}>
							{formatMessage(i18n.statusLabel)}
						</Box>
					) : (
						formatMessage(i18n.statusLabel)
					)}
				</Button>
			</Box>
		</div>
	);
};

export const ContentStateButton: FC<WithIntlProps<ContentStateButtonProps>> & {
	WrappedComponent: React.ComponentType<ContentStateButtonProps>;
} = injectIntl(ContentStateButtonComponent);
